// @refresh reset
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { type Language, LANGUAGES, t as translate, loadLanguage } from "@/lib/translations";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: "rtl" | "ltr";
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = typeof window !== "undefined" ? localStorage.getItem("souq-language") : null;
      const validCodes = LANGUAGES.map(l => l.code);
      return (validCodes.includes(saved as Language) ? saved as Language : "ar");
    } catch {
      return "ar";
    }
  });
  const [loadedLanguage, setLoadedLanguage] = useState<Language>("ar");

  const langInfo = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  const loadedLangInfo = LANGUAGES.find(l => l.code === loadedLanguage) || LANGUAGES[0];

  useEffect(() => {
    if (language === "ar") {
      setLoadedLanguage("ar");
      return;
    }
    loadLanguage(language).then(() => setLoadedLanguage(language));
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("souq-language", lang);
  }, []);

  useEffect(() => {
    document.documentElement.dir = langInfo?.dir ?? "rtl";
    document.documentElement.lang = language;
  }, [language, langInfo?.dir]);

  const tFn = useCallback((key: string) => translate(key, loadedLanguage), [loadedLanguage]);

  return (
    <LanguageContext.Provider value={{
      language: loadedLanguage,
      setLanguage,
      t: tFn,
      dir: loadedLangInfo?.dir ?? "rtl",
      isRTL: loadedLangInfo?.dir === "rtl",
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
